import { useMemo, useState } from "react";
import { BookmarkPlus, Search, X } from "lucide-react";
import { Button } from "@web/components/ui/button.tsx";
import { NamePromptDialog } from "@web/components/ConfirmDialog.tsx";
import {
  SearchableMultiCombobox,
  type ComboboxOption,
} from "@web/features/library/SearchableMultiCombobox.tsx";
import { cn } from "@web/lib/utils.ts";

export type FilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

type FacetKey = "artist" | "album" | "genre";

/**
 * Shared user-mode filter row: search + optional Artist / Album / Genre
 * multi-selects + removable chips. Callers own URL / query wiring.
 */
export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  artistOptions,
  albumOptions,
  genreOptions,
  artistIds = [],
  albumIds = [],
  genreIds = [],
  onArtistIdsChange,
  onAlbumIdsChange,
  onGenreIdsChange,
  extraChips = [],
  onClearAll,
  saveAsView,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  artistOptions?: readonly ComboboxOption[];
  albumOptions?: readonly ComboboxOption[];
  genreOptions?: readonly ComboboxOption[];
  artistIds?: readonly string[];
  albumIds?: readonly string[];
  genreIds?: readonly string[];
  onArtistIdsChange?: (ids: string[]) => void;
  onAlbumIdsChange?: (ids: string[]) => void;
  onGenreIdsChange?: (ids: string[]) => void;
  extraChips?: readonly FilterChip[];
  onClearAll?: () => void;
  saveAsView?: {
    busy?: boolean;
    onSave: (name: string) => void;
  };
}) {
  const [saveOpen, setSaveOpen] = useState(false);

  const showArtist = Boolean(artistOptions && onArtistIdsChange);
  const showAlbum = Boolean(albumOptions && onAlbumIdsChange);
  const showGenre = Boolean(genreOptions && onGenreIdsChange);

  const chips = useMemo(() => {
    const next: FilterChip[] = [...extraChips];
    const pushFacet = (
      facet: FacetKey,
      ids: readonly string[],
      options: readonly ComboboxOption[] | undefined,
      onChange: ((ids: string[]) => void) | undefined,
    ) => {
      if (!onChange || !options) return;
      const byId = new Map(options.map((o) => [o.id, o.label]));
      for (const id of ids) {
        next.push({
          key: `${facet}:${id}`,
          label: byId.get(id) ?? id,
          onRemove: () => onChange(ids.filter((x) => x !== id)),
        });
      }
    };
    pushFacet("artist", artistIds, artistOptions, onArtistIdsChange);
    pushFacet("album", albumIds, albumOptions, onAlbumIdsChange);
    pushFacet("genre", genreIds, genreOptions, onGenreIdsChange);
    return next;
  }, [
    extraChips,
    artistIds,
    albumIds,
    genreIds,
    artistOptions,
    albumOptions,
    genreOptions,
    onArtistIdsChange,
    onAlbumIdsChange,
    onGenreIdsChange,
  ]);

  const hasActive =
    searchValue.trim().length > 0 ||
    artistIds.length > 0 ||
    albumIds.length > 0 ||
    genreIds.length > 0 ||
    extraChips.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-48 flex-1 basis-48 sm:max-w-md">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" />
          <input
            type="search"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              "h-9 w-full rounded-full bg-panel-elevated pr-4 pl-10 text-sm text-text",
              "placeholder:text-faint focus-visible:outline-none",
            )}
          />
        </label>

        {showArtist && (
          <SearchableMultiCombobox
            label="Artist"
            options={artistOptions!}
            selectedIds={artistIds}
            onChange={onArtistIdsChange!}
          />
        )}
        {showAlbum && (
          <SearchableMultiCombobox
            label="Album"
            options={albumOptions!}
            selectedIds={albumIds}
            onChange={onAlbumIdsChange!}
          />
        )}
        {showGenre && (
          <SearchableMultiCombobox
            label="Genre"
            options={genreOptions!}
            selectedIds={genreIds}
            onChange={onGenreIdsChange!}
          />
        )}

        {hasActive && onClearAll && (
          <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>
            <X className="size-3.5" />
            Clear
          </Button>
        )}

        {hasActive && saveAsView && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={saveAsView.busy}
            onClick={() => setSaveOpen(true)}
          >
            <BookmarkPlus className="size-3.5" />
            Save as view
          </Button>
        )}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Active filters">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={cn(
                "inline-flex max-w-56 items-center gap-1 rounded-full border border-line",
                "bg-panel px-2.5 py-0.5 text-xs text-muted hover:border-muted hover:text-text",
              )}
              onClick={chip.onRemove}
              title={`Remove ${chip.label}`}
            >
              <span className="truncate">{chip.label}</span>
              <X className="size-3 shrink-0 text-faint" aria-hidden />
            </button>
          ))}
        </div>
      )}

      {saveAsView && (
        <NamePromptDialog
          open={saveOpen}
          title="Save as view"
          description="Create a saved view with the current filters."
          initialValue="Filtered view"
          confirmLabel="Save"
          busy={saveAsView.busy}
          onCancel={() => setSaveOpen(false)}
          onConfirm={(name) => {
            saveAsView.onSave(name);
            setSaveOpen(false);
          }}
        />
      )}
    </div>
  );
}
